import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin, Truck } from 'lucide-react';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
  'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

export default function AddressModal({ open, onClose, onSubmit, loading }) {
  const [address, setAddress] = useState({
    full_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
  });
  const [promoCode, setPromoCode] = useState('');

  const update = (field, value) => setAddress(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!address.full_name.trim() || !address.address_line1.trim() || !address.city.trim() || !address.state || !address.zip_code.trim()) {
      return;
    }
    onSubmit(address, promoCode.trim() || null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-edupaws-navy flex items-center gap-2">
            <Truck className="w-5 h-5 text-edupaws-sage" />
            Shipping Address
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500 font-body -mt-2 mb-4">
          Where should we ship your box?
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="font-body font-bold text-sm text-gray-700">Full Name</Label>
            <Input
              data-testid="address-full-name"
              required value={address.full_name}
              onChange={(e) => update('full_name', e.target.value)}
              placeholder="Jane Smith" className="mt-1"
            />
          </div>
          <div>
            <Label className="font-body font-bold text-sm text-gray-700">Address Line 1</Label>
            <Input
              data-testid="address-line1"
              required value={address.address_line1}
              onChange={(e) => update('address_line1', e.target.value)}
              placeholder="123 Main Street" className="mt-1"
            />
          </div>
          <div>
            <Label className="font-body font-bold text-sm text-gray-700">Address Line 2 <span className="font-normal text-gray-400">(Optional)</span></Label>
            <Input
              data-testid="address-line2"
              value={address.address_line2}
              onChange={(e) => update('address_line2', e.target.value)}
              placeholder="Apt 4B" className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">City</Label>
              <Input
                data-testid="address-city"
                required value={address.city}
                onChange={(e) => update('city', e.target.value)}
                placeholder="Austin" className="mt-1"
              />
            </div>
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">State</Label>
              <select
                data-testid="address-state"
                required value={address.state}
                onChange={(e) => update('state', e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-gray-200 px-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-edupaws-sage"
              >
                <option value="">Select</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">ZIP Code</Label>
              <Input
                data-testid="address-zip"
                required value={address.zip_code}
                onChange={(e) => update('zip_code', e.target.value)}
                placeholder="78701" className="mt-1"
              />
            </div>
            <div>
              <Label className="font-body font-bold text-sm text-gray-700">Country</Label>
              <Input
                data-testid="address-country"
                value="United States" disabled
                className="mt-1 bg-gray-50"
              />
            </div>
          </div>
          <div>
            <Label className="font-body font-bold text-sm text-gray-700">Promo Code <span className="font-normal text-gray-400">(Optional)</span></Label>
            <Input
              data-testid="address-promo-code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="Enter code" className="mt-1 uppercase"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="address-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-edupaws-navy text-white font-heading font-bold py-3 rounded-full
                       shadow-[0_4px_0_#2D3B47] hover:shadow-[0_2px_0_#2D3B47] hover:translate-y-[2px] transition-all disabled:opacity-50"
          >
            <MapPin className="w-4 h-4" />
            {loading ? 'Processing...' : 'Continue to Payment'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
